import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ListSubheader,
  Checkbox,
  ListItemText,
  TextField,
  FormControlLabel
} from '@mui/material';
import { default as CloseIcon } from '@mui/icons-material/Close';
import { format } from 'date-fns';
import type { AnimalGroup } from '../../../../types/animal';

export interface BulkTreatmentData {
  selectedAnimals: string[];
  treatmentFrequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  treatmentStartDate: string;
  treatmentEndDate: string;
  customTreatmentDays: string[];
  treatmentRepeatCount: number;
  enableNotifications: boolean;
  title: string;
  description: string;
  performedBy: string;
}

interface BulkTreatmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: BulkTreatmentData) => void;
  animalGroups: AnimalGroup[];
  initialTitle?: string;
  initialDescription?: string;
  initialPerformedBy?: string;
}

const BulkTreatmentDialog: React.FC<BulkTreatmentDialogProps> = ({
  open,
  onClose,
  onSave,
  animalGroups,
  initialTitle = '',
  initialDescription = '',
  initialPerformedBy = ''
}) => {
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [treatmentFrequency, setTreatmentFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('daily');
  const [treatmentStartDate, setTreatmentStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [treatmentEndDate, setTreatmentEndDate] = useState('');
  const [customTreatmentDays, setCustomTreatmentDays] = useState<string[]>([]);
  const [treatmentRepeatCount, setTreatmentRepeatCount] = useState(1);
  const [enableNotifications, setEnableNotifications] = useState(true);
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [performedBy, setPerformedBy] = useState(initialPerformedBy);

  // Update local state when props change or dialog opens?
  // For simplicity, we rely on initial props. If the dialog is reused without unmounting,
  // we might need useEffect to reset state when `open` changes to true.
  // But let's keep it simple first.
  
  React.useEffect(() => {
    if (open) {
      // Reset or sync state when opened
      // We might want to clear previous selections
      // But keeping previous selection might be desired?
      // The original code cleared `selectedAnimals` in `handleCloseMedicalRecordDialog`.
      // So we should probably clear it here when opening or closing.
      // Let's rely on the parent to unmount or we reset on open.
      // Actually, if we want to mimic `handleCloseMedicalRecordDialog` clearing it:
      // The parent handles `onClose`, so we can reset there?
      // No, better to reset internally when `open` becomes true if we want a fresh state.
      // Or just reset on Close.
    }
  }, [open]);

  const handleSave = () => {
    onSave({
      selectedAnimals,
      treatmentFrequency,
      treatmentStartDate,
      treatmentEndDate,
      customTreatmentDays,
      treatmentRepeatCount,
      enableNotifications,
      title,
      description,
      performedBy
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Schedule Bulk Treatment</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Animals</InputLabel>
              <Select
                multiple
                value={selectedAnimals}
                onChange={(e) => setSelectedAnimals(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={animalGroups.find(g => g.individuals.some(a => a.id === value))?.individuals.find(a => a.id === value)?.name} />
                    ))}
                  </Box>
                )}
              >
                {animalGroups.map((group) => (
                  <React.Fragment key={group.id}>
                    <ListSubheader>{group.type} - {group.breed}</ListSubheader>
                    {group.individuals.map((animal) => (
                      <MenuItem key={animal.id} value={animal.id}>
                        <Checkbox checked={selectedAnimals.indexOf(animal.id) > -1} />
                        <ListItemText primary={animal.name} secondary={`ID: ${animal.tagId || animal.id}`} />
                      </MenuItem>
                    ))}
                  </React.Fragment>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Treatment Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Performed By"
              value={performedBy}
              onChange={(e) => setPerformedBy(e.target.value)}
              placeholder="Veterinarian or Staff Name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Treatment Frequency</InputLabel>
              <Select
                value={treatmentFrequency}
                onChange={(e) => setTreatmentFrequency(e.target.value as 'daily' | 'weekly' | 'monthly' | 'custom')}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="custom">Custom Schedule</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={treatmentStartDate}
              onChange={(e) => setTreatmentStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={treatmentEndDate}
              onChange={(e) => setTreatmentEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {treatmentFrequency === 'custom' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Custom Treatment Days"
                placeholder="Enter dates (YYYY-MM-DD) separated by commas"
                value={customTreatmentDays.join(', ')}
                onChange={(e) => setCustomTreatmentDays(e.target.value.split(',').map(d => d.trim()))}
                helperText="Enter dates in YYYY-MM-DD format, separated by commas"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Repeat Count"
              value={treatmentRepeatCount}
              onChange={(e) => setTreatmentRepeatCount(parseInt(e.target.value))}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableNotifications}
                  onChange={(e) => setEnableNotifications(e.target.checked)}
                />
              }
              label="Enable treatment notifications"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Treatment Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Schedule Treatment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkTreatmentDialog;
